import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertBadgeRegular, Book20Regular, Building20Regular, Chat20Regular, Checkmark20Regular, ChevronRight16Regular, Dismiss24Regular,
  Keyboard20Regular, Person20Regular, QuestionCircle20Regular, Settings20Regular, SettingsRegular, SignOut20Regular, VideoSecurity20Regular,
} from '@fluentui/react-icons';
import useAuth from '../../../../hooks/useAuth';
import { useNotifications } from '../../../header/Notificationsstore';
import styles from './Headerallapps.module.css';

/**
 * Top bar of the All Apps screen: the signed-in user, a greeting, notifications and settings.
 * The account sheet carries the same things as the desktop header menus (Hyper View, Help, Settings, My account, Sign out).
 * New CSS Module; the old Header.css is not used here.
 */
const AVATAR_COLORS = ['#0f6cbd', '#7a3fb5', '#1e7a3c', '#c2570c', '#0b6a6a', '#b8791a', '#c4314b', '#2b5fd9'];
const colorOf = (s: string) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

const greeting = (d: Date) => {
  const h = d.getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};

type SheetKind = 'notes' | 'settings' | 'account' | null;

const Sheet: React.FC<{ title: string; onClose: () => void; children: React.ReactNode; action?: React.ReactNode }> = ({ title, onClose, children, action }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className={styles.layer}>
      <div className={styles.scrim} onClick={onClose} />
      <div className={styles.sheet} role="dialog" aria-modal="true" aria-label={title}>
        <div className={styles.grab} />
        <div className={styles.sheetHead}>
          <h2>{title}</h2>
          {action}
          <button type="button" className={styles.round} aria-label="Close" onClick={onClose}><Dismiss24Regular /></button>
        </div>
        <div className={styles.sheetBody}>{children}</div>
      </div>
    </div>
  );
};

const Item: React.FC<{ icon: React.ReactNode; label: string; note?: string; onClick: () => void; danger?: boolean }> = ({ icon, label, note, onClick, danger }) => (
  <button type="button" className={`${styles.item} ${danger ? styles.itemDanger : ''}`} onClick={onClick}>
    <span className={styles.itemIcon}>{icon}</span>
    <span className={styles.itemText}>
      <b>{label}</b>
      {note && <small>{note}</small>}
    </span>
    {!danger && <ChevronRight16Regular className={styles.chev} />}
  </button>
);

const Header: React.FC = () => {
  const { user, logout } = useAuth() as unknown as { user?: { firstName?: string; lastName?: string; email?: string; token?: string }; logout?: () => void };
  const navigate = useNavigate();
  const [now, setNow] = useState(() => new Date());
  const [sheet, setSheet] = useState<SheetKind>(null);
  const notes = useNotifications((s) => s.notes);
  const markRead = useNotifications((s) => s.markRead);
  const markAllRead = useNotifications((s) => s.markAllRead);
  const unread = notes.filter((n) => !n.read).length;

  // keep the greeting right if the app stays open across the day
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 60000);
    return () => window.clearInterval(t);
  }, []);

  const first = (user?.firstName ?? '').trim();
  const last = (user?.lastName ?? '').trim();
  const email = (user?.email ?? '').trim();
  const fromEmail = (email.split('@')[0] ?? '').replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const full = `${first} ${last}`.trim() || fromEmail || 'Welcome';
  const shortName = first || fromEmail.split(' ')[0] || '';
  const initials = (first && last ? first[0] + last[0] : full.slice(0, 2)).toUpperCase();
  const tint = colorOf(full);

  const go = (path: string) => {
    setSheet(null);
    navigate(path);
  };
  const signOut = () => {
    setSheet(null);
    if (logout) logout();
    else navigate('/login');
  };
  const close = () => setSheet(null);

  return (
    <>
      <header className={styles.bar}>
        <button type="button" className={styles.avatarBtn} aria-label="Account menu" onClick={() => setSheet('account')}>
          <span className={styles.avatar} style={{ background: `${tint}1f`, color: tint, borderColor: `${tint}55` }} aria-hidden="true">
            {initials}
          </span>
        </button>
        <div className={styles.who}>
          <span className={styles.hello}>{greeting(now)}{shortName ? ',' : ''}</span>
          <span className={styles.name}>{shortName || full}</span>
        </div>
        <button type="button" className={styles.round} aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`} onClick={() => setSheet('notes')}>
          <AlertBadgeRegular />
          {unread > 0 && <span className={styles.badge}>{unread > 9 ? '9+' : unread}</span>}
        </button>
        <button type="button" className={styles.round} aria-label="Settings" onClick={() => setSheet('settings')}>
          <SettingsRegular />
        </button>
      </header>

      {sheet === 'notes' && (
        <Sheet
          title="Notifications"
          onClose={close}
          action={<button type="button" className={styles.textBtn} disabled={!unread} onClick={markAllRead}><Checkmark20Regular /> Mark all read</button>}
        >
          {notes.length === 0 && <p className={styles.empty}>You&apos;re all caught up</p>}
          {notes.map((n) => (
            <button key={n.id} type="button" className={`${styles.note} ${n.read ? '' : styles.noteNew}`} onClick={() => markRead(n.id)}>
              <span className={styles.noteDot} />
              <span className={styles.noteText}>
                <b>{n.title}</b>
                <span>{n.body}</span>
                <small>{n.time}</small>
              </span>
            </button>
          ))}
          <button type="button" className={styles.wide} onClick={() => go('/notifications')}>View all notifications</button>
        </Sheet>
      )}

      {sheet === 'settings' && (
        <Sheet title="Settings" onClose={close}>
          <Item icon={<Settings20Regular />} label="All settings" note="Set up your workspace and apps" onClick={() => go('/settings')} />
          <Item icon={<Person20Regular />} label="My profile" onClick={() => go('/settings/profile')} />
          <Item icon={<Building20Regular />} label="Company" onClick={() => go('/settings/company')} />
        </Sheet>
      )}

      {sheet === 'account' && (
        <Sheet title="Account" onClose={close}>
          <div className={styles.who2}>
            <span className={styles.avatar} style={{ background: `${tint}1f`, color: tint, borderColor: `${tint}55` }} aria-hidden="true">{initials}</span>
            <span>
              <b>{full}</b>
              <small>{email}</small>
            </span>
          </div>
          <Item icon={<Person20Regular />} label="My account" onClick={() => go('/settings/profile')} />
          <Item icon={<Settings20Regular />} label="Settings" onClick={() => go('/settings')} />
          <Item icon={<VideoSecurity20Regular />} label="Hyper View" onClick={() => go('/hyperview')} />
          <p className={styles.group}>Help</p>
          <Item icon={<QuestionCircle20Regular />} label="Help center" onClick={() => go('/help')} />
          <Item icon={<Book20Regular />} label="User guides" onClick={() => go('/help/guides')} />
          <Item icon={<Keyboard20Regular />} label="Keyboard shortcuts" onClick={() => go('/help/shortcuts')} />
          <Item icon={<Chat20Regular />} label="Contact support" onClick={() => go('/contact')} />
          <Item icon={<SignOut20Regular />} label="Sign out" danger onClick={signOut} />
        </Sheet>
      )}
    </>
  );
};

export default Header;